import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
	Box,
	CircularProgress,
	Typography,
	IconButton,
	Button,
	Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useFirebase } from "../context/ApiContext";
import PassportCover from "../components/Passport/PassportCover";
import PassportIndex from "../components/Passport/PassportIndex";
import PassportCountryPage from "../components/Passport/PassportCountryPage";
import PassportBackCover from "../components/Passport/PassportBackCover";

const STAMPS_PER_PAGE = 12;

const PassportViewerPage = () => {
	const { token } = useParams();
	const navigate = useNavigate();
	const { getSharedPassport } = useFirebase();

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [passport, setPassport] = useState(null);
	const [countries, setCountries] = useState([]);
	const [currentPageIndex, setCurrentPageIndex] = useState(0);
	const [direction, setDirection] = useState("next");

	const touchStartX = useRef(0);
	const touchStartY = useRef(0);

	// Cargar pasaporte compartido
	useEffect(() => {
		const loadSharedPassport = async () => {
			try {
				const data = await getSharedPassport(token);
				if (data && data.passport) {
					setPassport(data.passport);
					setCountries(data.passport.countries || []);
				} else {
					setError("No se pudo cargar el pasaporte");
				}
			} catch (err) {
				console.error("Error cargando pasaporte compartido:", err);
				if (err?.response?.status === 401) {
					setError("Este pasaporte es privado. Debes iniciar sesión para acceder.");
				} else if (err?.response?.status === 403) {
					setError("No tienes permiso para acceder a este pasaporte.");
				} else if (err?.response?.status === 404) {
					setError("Pasaporte no encontrado.");
				} else {
					setError("Error al cargar el pasaporte.");
				}
			} finally {
				setLoading(false);
			}
		};
		loadSharedPassport();
	}, [token]);

	// Agrupar por país (countryCode): misma lógica que PassportPage. Normalizar código (trim + mayúsculas)
	// y ordenar grupos y visitas por "order" para que coincida con la vista de edición y no se repitan países.
	const uniqueCountries = useMemo(() => {
		const byCode = {};
		(countries || []).forEach((stamp) => {
			const code = (stamp.countryCode || "").trim().toUpperCase();
			if (!code) return;
			if (!byCode[code]) {
				byCode[code] = {
					countryCode: stamp.countryCode?.trim() || stamp.countryCode,
					countryName: stamp.countryName,
					visits: [],
				};
			}
			byCode[code].visits.push(stamp);
		});
		const groups = Object.values(byCode);
		// Ordenar visitas dentro de cada grupo por "order"
		groups.forEach((g) => {
			g.visits.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
		});
		// Ordenar grupos por el primer "order" de cada país (igual que el backend)
		groups.sort((a, b) => {
			const orderA = Math.min(...a.visits.map((v) => v.order ?? 999999));
			const orderB = Math.min(...b.visits.map((v) => v.order ?? 999999));
			return orderA - orderB;
		});
		return groups;
	}, [countries]);

	const numUniqueCountries = uniqueCountries.length;

	// ---------- Paginación ----------
	const indexPageCount = useMemo(
		() => Math.max(1, Math.ceil(numUniqueCountries / STAMPS_PER_PAGE)),
		[numUniqueCountries]
	);

	const needAddOnlyPage = false; // Viewer nunca muestra botón "Añadir"
	const totalIndexPages = indexPageCount;
	const totalPages = 1 + totalIndexPages + numUniqueCountries + 1;

	const firstIndexPage = 1;
	const firstCountryPage = 1 + totalIndexPages;
	const backCoverPage = totalPages - 1;

	const handlePrev = useCallback(() => {
		setDirection("prev");
		setCurrentPageIndex((prev) => Math.max(0, prev - 1));
	}, []);

	const handleNext = useCallback(() => {
		setDirection("next");
		setCurrentPageIndex((prev) => Math.min(totalPages - 1, prev + 1));
	}, [totalPages]);

	// Swipe
	const handleTouchStart = (e) => {
		touchStartX.current = e.touches[0].clientX;
		touchStartY.current = e.touches[0].clientY;
	};

	const handleTouchEnd = (e) => {
		const deltaX = e.changedTouches[0].clientX - touchStartX.current;
		const deltaY = e.changedTouches[0].clientY - touchStartY.current;
		if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
			if (deltaX < 0) handleNext();
			else handlePrev();
		}
	};

	// Keyboard
	useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.key === "ArrowLeft") handlePrev();
			if (e.key === "ArrowRight") handleNext();
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [handlePrev, handleNext]);

	// Navegar a un país
	const handleNavigateToCountry = (globalCountryIndex) => {
		setDirection("next");
		setCurrentPageIndex(firstCountryPage + globalCountryIndex);
	};

	// ---------- Renders ----------

	if (loading) {
		return (
			<Box
				sx={{
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					background: "#f7f7f7",
				}}
			>
				<Box sx={{ textAlign: "center" }}>
					<CircularProgress sx={{ color: "#8b6914" }} size={48} />
					<Typography sx={{ mt: 2, color: "#5c4a32", fontFamily: "'Playfair Display', serif" }}>
						Cargando pasaporte...
					</Typography>
				</Box>
			</Box>
		);
	}

	if (error) {
		return (
			<Box
				sx={{
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					background: "#f7f7f7",
				}}
			>
				<Box sx={{ textAlign: "center", p: 4, maxWidth: 400 }}>
					<Typography
						variant="h6"
						sx={{ color: "#5c4a32", mb: 2, fontFamily: "'Playfair Display', serif" }}
					>
						{error}
					</Typography>
					<Button
						variant="contained"
						onClick={() => navigate("/")}
						sx={{
							background: "linear-gradient(135deg, #8b6914 0%, #a07820 100%)",
							color: "#fff",
							fontWeight: 600,
						}}
					>
						Ir al inicio
					</Button>
				</Box>
			</Box>
		);
	}

	// Renderizar página actual (solo lectura)
	const renderCurrentPage = () => {
		if (currentPageIndex === 0) {
			return <PassportCover passport={passport} countriesCount={countries.length} />;
		}

		if (currentPageIndex === backCoverPage) {
			return <PassportBackCover countriesCount={countries.length} />;
		}

		if (currentPageIndex >= firstIndexPage && currentPageIndex < firstCountryPage) {
			const idxPage = currentPageIndex - firstIndexPage;
			const start = idxPage * STAMPS_PER_PAGE;
			const end = Math.min(start + STAMPS_PER_PAGE, numUniqueCountries);
			const slice = uniqueCountries.slice(start, end);

			return (
				<PassportIndex
					countries={slice}
					globalOffset={start}
					totalCountries={numUniqueCountries}
					currentIndexPage={idxPage}
					totalIndexPages={totalIndexPages}
					isLastIndexPage={idxPage === totalIndexPages - 1}
					onNavigate={handleNavigateToCountry}
					onAddCountry={null}
					readOnly
				/>
			);
		}

		const countryIndex = currentPageIndex - firstCountryPage;
		const group = uniqueCountries[countryIndex];
		if (!group || !group.visits?.length) return null;
		const firstVisit = group.visits[0];

		return (
			<PassportCountryPage
				country={firstVisit}
				visits={group.visits}
				pageNumber={currentPageIndex}
				onUpdate={() => {}}
				onDelete={() => {}}
				onAddPhoto={() => {}}
				onDeletePhoto={() => {}}
				readOnly
			/>
		);
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				background: "#f7f7f7",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				position: "relative",
				overflow: "hidden",
			}}
		>
			{/* Fondo */}
			<Box
				sx={{
					position: "fixed",
					inset: 0,
					backgroundImage: `
						radial-gradient(circle at 20% 20%, rgba(0, 0, 0, 0.02) 0%, transparent 40%),
						radial-gradient(circle at 80% 80%, rgba(0, 0, 0, 0.015) 0%, transparent 40%)
					`,
					pointerEvents: "none",
					zIndex: 0,
				}}
			/>

			{/* Botón volver */}
			<Box sx={{ position: "fixed", top: 16, left: 16, zIndex: 100 }}>
				<Tooltip title="Volver">
					<IconButton
						onClick={() => navigate("/")}
						sx={{
							color: "#5c4a32",
							backgroundColor: "rgba(92, 74, 50, 0.08)",
							border: "1px solid rgba(92, 74, 50, 0.15)",
							"&:hover": { backgroundColor: "rgba(92, 74, 50, 0.12)" },
						}}
					>
						<ArrowBackIosNewIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>

			{/* Contador de páginas */}
			<Box
				sx={{
					position: "fixed",
					top: 16,
					right: 16,
					zIndex: 100,
					display: "flex",
					alignItems: "center",
					gap: 1,
				}}
			>
				<Typography
					sx={{
						color: "#5c4a32",
						fontSize: "0.85rem",
						fontFamily: "'Playfair Display', serif",
						letterSpacing: "0.05em",
					}}
				>
					{currentPageIndex + 1} / {totalPages}
				</Typography>
			</Box>

			{/* Contenedor del pasaporte */}
			<Box
				onTouchStart={handleTouchStart}
				onTouchEnd={handleTouchEnd}
				sx={{
					width: "100%",
					maxWidth: 550,
					aspectRatio: "21 / 29.7",
					maxHeight: "calc(100vh - 100px)",
					position: "relative",
					zIndex: 1,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					mx: "auto",
					px: { xs: 2, sm: 3 },
				}}
			>
				<Box
					sx={{
						width: "100%",
						height: "100%",
						borderRadius: { xs: "12px", sm: "16px" },
						boxShadow: "0 8px 40px rgba(0,0,0,0.13), 0 2px 8px rgba(0,0,0,0.08)",
						overflow: "hidden",
						position: "relative",
						"@keyframes passportSlideNext": {
							from: { transform: "translateX(100%)", opacity: 0 },
							to: { transform: "translateX(0)", opacity: 1 },
						},
						"@keyframes passportSlidePrev": {
							from: { transform: "translateX(-100%)", opacity: 0 },
							to: { transform: "translateX(0)", opacity: 1 },
						},
					}}
				>
					<Box
						key={currentPageIndex}
						sx={{
							width: "100%",
							height: "100%",
							animation:
								direction === "next"
									? "passportSlideNext 0.4s ease-in-out"
									: "passportSlidePrev 0.4s ease-in-out",
						}}
					>
						{renderCurrentPage()}
					</Box>
				</Box>
			</Box>

			{/* Navegación inferior */}
			<Box
				sx={{
					position: "fixed",
					bottom: 16,
					left: 0,
					right: 0,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					gap: 2,
					zIndex: 100,
				}}
			>
				<IconButton
					onClick={handlePrev}
					disabled={currentPageIndex === 0}
					sx={{
						color: "#5c4a32",
						backgroundColor: "rgba(92, 74, 50, 0.08)",
						border: "1px solid rgba(92, 74, 50, 0.15)",
						"&:hover": { backgroundColor: "rgba(92, 74, 50, 0.12)" },
						"&.Mui-disabled": { opacity: 0.3 },
					}}
				>
					<ArrowBackIcon />
				</IconButton>

				{/* Dots */}
				<Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
					{Array.from({ length: Math.min(totalPages, 15) }).map((_, i) => {
						const pageIdx =
							totalPages <= 15
								? i
								: Math.round((i / 14) * (totalPages - 1));
						return (
							<Box
								key={i}
								onClick={() => {
									setDirection(pageIdx > currentPageIndex ? "next" : "prev");
									setCurrentPageIndex(pageIdx);
								}}
								sx={{
									width: pageIdx === currentPageIndex ? 12 : 6,
									height: 6,
									borderRadius: 3,
									backgroundColor:
										pageIdx === currentPageIndex
											? "#8b6914"
											: "rgba(92, 74, 50, 0.2)",
									transition: "all 0.3s ease",
									cursor: "pointer",
								}}
							/>
						);
					})}
				</Box>

				<IconButton
					onClick={handleNext}
					disabled={currentPageIndex === totalPages - 1}
					sx={{
						color: "#5c4a32",
						backgroundColor: "rgba(92, 74, 50, 0.08)",
						border: "1px solid rgba(92, 74, 50, 0.15)",
						"&:hover": { backgroundColor: "rgba(92, 74, 50, 0.12)" },
						"&.Mui-disabled": { opacity: 0.3 },
					}}
				>
					<ArrowForwardIcon />
				</IconButton>
			</Box>
		</Box>
	);
};

export default PassportViewerPage;
